// apps/client/src/components/layout/LeadCaptureTopNav.tsx
// Contextual horizontal top nav for the Lead Capture add-on subapp.
// 9 page tabs matching the mockup (lead-capture-integration.html).

import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  HelpCircle,
  Eye,
  Inbox,
  BarChart3,
  Mail,
  LayoutGrid,
  CheckCircle,
  Workflow,
  DollarSign,
  Clock,
  Cog,
  GitBranch,
  Tags,
  Layers,
  MessageSquare,
  Megaphone,
  Users,
  Gauge,
  Mails,
  Globe,
  ScrollText,
  BellOff,
  Lightbulb,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCapability } from '../../hooks/useCapability';
import {
  LEAD_TAB_ACCESS,
  LEAD_TAB_ORDER,
  canAccessLeadTab,
} from '../../config/leadTabAccess';

interface TabDef {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
}

interface LeadCaptureTopNavProps {
  inboxBadge?: number;
  setupStepsComplete?: number;
  setupStepsTotal?: number;
  /**
   * `featureFlags.edm_broadcast === true` for the company. The flag lives on
   * company data, which this nav cannot read, so the layout passes it in.
   * Absent means off: the eDM tab is never shown by default.
   */
  edmEnabled?: boolean;
  /**
   * `featureFlags.growth === 'propose'` for the company, passed by the layout
   * for the same reason. Absent means off: the Growth tab is never shown by
   * default.
   */
  growthProposeEnabled?: boolean;
}

export const LeadCaptureTopNav: React.FC<LeadCaptureTopNavProps> = ({
  inboxBadge,
  setupStepsComplete,
  setupStepsTotal,
  edmEnabled = false,
  growthProposeEnabled = false,
}) => {
  const { claims } = useAuth();
  const role = claims?.role || '';
  const allowedLeadPages = Array.isArray(claims?.allowedLeadPages)
    ? new Set<string>(claims!.allowedLeadPages as string[])
    : null;

  // The DM Inbox is the one tab decided by a capability rather than a role
  // list. useCapability is a hook, so it is resolved here and handed to the
  // shared access map as a predicate.
  const canSeeDmInbox = useCapability(
    'access_conversations_inbox',
    LEAD_TAB_ACCESS['/leads/dm-inbox'].roles,
  );
  const hasCapability = React.useCallback(
    (capability: string) =>
      capability === 'access_conversations_inbox' ? canSeeDmInbox : false,
    [canSeeDmInbox],
  );

  // Setup badge. `setupStepsTotal` is passed by the route: marketing walks a
  // shorter wizard than an administrator, so a hardcoded denominator showed
  // the wrong fraction. The badge renders only when the route supplies BOTH
  // numbers.
  const setupBadge =
    setupStepsComplete !== undefined &&
    setupStepsTotal !== undefined &&
    setupStepsComplete < setupStepsTotal
      ? `${setupStepsComplete}/${setupStepsTotal}`
      : undefined;

  const TAB_META: Record<
    string,
    { label: string; icon: React.ComponentType<{ className?: string }>; badge?: string | number }
  > = {
    '/leads/how-it-works': { label: 'How It Works', icon: HelpCircle },
    '/leads/sources': { label: 'Lead Sources', icon: Eye },
    '/leads/inbox': { label: 'Lead Inbox', icon: Inbox, badge: inboxBadge },
    '/leads/dm-inbox': { label: 'DM Inbox', icon: MessageSquare },
    '/leads/attribution': { label: 'Attribution', icon: BarChart3 },
    '/leads/monthly-report': { label: 'Monthly Report', icon: Megaphone },
    '/leads/email': { label: 'Email Automation', icon: Mail },
    '/leads/templates': { label: 'Templates', icon: LayoutGrid },
    '/leads/edm': { label: 'eDM Campaigns', icon: Mails },
    '/leads/website': { label: 'Website', icon: Globe },
    '/leads/consent': { label: 'Consent', icon: ScrollText },
    '/leads/unsubscribes': { label: 'Unsubscribes', icon: BellOff },
    '/leads/setup': { label: 'Setup Wizard', icon: CheckCircle, badge: setupBadge },
    '/leads/integrations': { label: 'Integrations', icon: Workflow },
    '/leads/staff': { label: 'Staff & Services', icon: Users },
    '/leads/taxonomy': { label: 'Taxonomy', icon: Tags },
    '/leads/routing': { label: 'Routing Rules', icon: GitBranch },
    '/leads/pipeline': { label: 'Pipeline', icon: Layers },
    '/leads/growth': { label: 'Growth', icon: Lightbulb },
    '/leads/crm-mappings': { label: 'CRM Mappings', icon: Cog },
    '/leads/sla': { label: 'Lead SLA', icon: Gauge },
    '/leads/availability': { label: 'My Availability', icon: Clock },
    '/leads/cost-savings': { label: 'Cost Savings', icon: DollarSign },
  };

  // Visibility comes from the SAME map the route guard uses, so a tab can
  // never be shown for a page the guard would then refuse (or hidden on a
  // page the user can legitimately reach). The eDM and Growth tabs are
  // additionally dropped while their company flag is off, before any role check.
  const visibleTabs: TabDef[] = LEAD_TAB_ORDER.filter(
    (path) => (path !== '/leads/edm' || edmEnabled) && (path !== '/leads/growth' || growthProposeEnabled),
  )
    .filter((path) => canAccessLeadTab(path, role, allowedLeadPages, hasCapability))
    .map((path) => ({ to: path, ...TAB_META[path] }));

  return (
    <div className="border-b border-gray-700 bg-gray-900/60">
      <div className="flex gap-1 overflow-x-auto px-2 py-1">
        {visibleTabs.map(({ to, label, icon: Icon, badge }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/60 border border-transparent'
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
            {badge !== undefined && (
              <span
                className={`ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[0.65rem] font-semibold ${
                  typeof badge === 'string' && badge.includes('/')
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                }`}
              >
                {badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default LeadCaptureTopNav;
